import type { SimpleProduct } from "../../types/product";
import type { ShapeInput } from "../../types/shape";
import type Shape from "../models/Shape";
import PlateArrow from "../models/Shape/Plate/Arrow";
import PlateBarrel from "../models/Shape/Plate/Barrel";
import PlateCircleComplete from "../models/Shape/Plate/Circle/Complete";
import PlateCircleHalf from "../models/Shape/Plate/Circle/Half";
import PlateCircleQuarter from "../models/Shape/Plate/Circle/Quarter";
import PlateDrop from "../models/Shape/Plate/Drop";
import PlateEgg from "../models/Shape/Plate/Egg";
import PlateGem from "../models/Shape/Plate/Gem";
import PlateHeart from "../models/Shape/Plate/Heart";
import PlateHexagon from "../models/Shape/Plate/Hexagon";
import PlateKite from "../models/Shape/Plate/Kite";
import PlateOvalDanish from "../models/Shape/Plate/Oval/Danish";
import PlateOvalFlat from "../models/Shape/Plate/Oval/Flat";
import PlateOvalRegular from "../models/Shape/Plate/Oval/Regular";
import PlateParallelogram from "../models/Shape/Plate/Parallelogram";
import PlatePolygon from "../models/Shape/Plate/Polygon";

/** Plates */
import PlateRectangleAngledLength from "../models/Shape/Plate/Rectangle/Angled/Length";
import PlateRectangleAngledWidth from "../models/Shape/Plate/Rectangle/Angled/Width";
import PlateRectangleBevelledCorners from "../models/Shape/Plate/Rectangle/Bevelled/Corners";
import PlateRectangleBevelledCornersFour from "../models/Shape/Plate/Rectangle/Bevelled/Corners/Four";
import PlateRectangleBevelledCornersTwo from "../models/Shape/Plate/Rectangle/Bevelled/Corners/Two";
import PlateRectangleComplete from "../models/Shape/Plate/Rectangle/Complete";
import PlateRectangleCustom from "../models/Shape/Plate/Rectangle/Custom";
import PlateRectangleRoundedCorners from "../models/Shape/Plate/Rectangle/Rounded/Corners";
import PlateRectangleRoundedCornersFour from "../models/Shape/Plate/Rectangle/Rounded/Corners/Four";
import PlateRectangleRoundedCornersTwo from "../models/Shape/Plate/Rectangle/Rounded/Corners/Two";
import PlateRectangleSquare from "../models/Shape/Plate/Rectangle/Square";
import PlateStar from "../models/Shape/Plate/Star";
import PlateTrapezium from "../models/Shape/Plate/Trapezium";
import PlateTriangleRectangular from "../models/Shape/Plate/Triangle/Rectangular";
import PlateTriangleRegular from "../models/Shape/Plate/Triangle/Regular";
import PlateTriangleSymmetrical from "../models/Shape/Plate/Triangle/Symmetrical";

/** Shelfs */
import ShelfComplete from "../models/Shape/Shelf/Complete";
import ShelfCustom from "../models/Shape/Shelf/Custom";

/** Skirting */
import SkirtingBlock from "../models/Shape/Skirting/Block";
import SkirtingFrameComplete from "../models/Shape/Skirting/Frame/Complete";
import SkirtingFrameCustom from "../models/Shape/Skirting/Frame/Custom";
import SkirtingLyingComplete from "../models/Shape/Skirting/Lying/Complete";
import SkirtingLyingCustom from "../models/Shape/Skirting/Lying/Custom";
import SkirtingStandingComplete from "../models/Shape/Skirting/Standing/Complete";
import SkirtingStandingCustom from "../models/Shape/Skirting/Standing/Custom";
import ThreedCubeClosed from "../models/Shape/ThreeD/Cube/Closed";
import ThreedCubeOpen from "../models/Shape/ThreeD/Cube/Open";

/** ThreeD */
import ThreedHollowshelf from "../models/Shape/ThreeD/Hollowshelf";
import ThreedPillarClosed from "../models/Shape/ThreeD/Pillar/Closed";
import ThreedPillarOpen from "../models/Shape/ThreeD/Pillar/Open";

/** Beam */
import type { ProductionUnit } from "@tosize/ui";
import BeamComplete from "../models/Shape/Beam/Complete";
import BeamShortened from "../models/Shape/Beam/Shortened";

const ShapeFactory = {
	getInstance(input: ShapeInput, unit?: ProductionUnit, product?: SimpleProduct): Shape {
		switch (input.code) {
			/** Plate */
			case PlateRectangleAngledLength.code:
				return new PlateRectangleAngledLength(input, unit, product);
			case PlateRectangleAngledWidth.code:
				return new PlateRectangleAngledWidth(input, unit, product);
			case PlateRectangleBevelledCornersTwo.code:
				return new PlateRectangleBevelledCornersTwo(input, unit, product);
			case PlateRectangleBevelledCornersFour.code:
				return new PlateRectangleBevelledCornersFour(input, unit, product);
			case PlateRectangleBevelledCorners.code:
				return new PlateRectangleBevelledCorners(input, unit, product);
			case PlateRectangleRoundedCornersTwo.code:
				return new PlateRectangleRoundedCornersTwo(input, unit, product);
			case PlateRectangleRoundedCornersFour.code:
				return new PlateRectangleRoundedCornersFour(input, unit, product);
			case PlateRectangleRoundedCorners.code:
				return new PlateRectangleRoundedCorners(input, unit, product);
			case PlateRectangleComplete.code:
				return new PlateRectangleComplete(input, unit, product);
			case PlateRectangleCustom.code:
				return new PlateRectangleCustom(input, unit, product);
			case PlateRectangleSquare.code:
				return new PlateRectangleSquare(input, unit, product);
			case PlateTriangleRectangular.code:
				return new PlateTriangleRectangular(input, unit, product);
			case PlateTriangleRegular.code:
				return new PlateTriangleRegular(input, unit, product);
			case PlateTriangleSymmetrical.code:
				return new PlateTriangleSymmetrical(input, unit, product);
			case PlateCircleComplete.code:
				return new PlateCircleComplete(input, unit, product);
			case PlateCircleHalf.code:
				return new PlateCircleHalf(input, unit, product);
			case PlateCircleQuarter.code:
				return new PlateCircleQuarter(input, unit, product);
			case PlateOvalRegular.code:
				return new PlateOvalRegular(input, unit, product);
			case PlateOvalFlat.code:
				return new PlateOvalFlat(input, unit, product);
			case PlateOvalDanish.code:
				return new PlateOvalDanish(input, unit, product);
			case PlateBarrel.code:
				return new PlateBarrel(input, unit, product);
			case PlateDrop.code:
				return new PlateDrop(input, unit, product);
			case PlateGem.code:
				return new PlateGem(input, unit, product);
			case PlateEgg.code:
				return new PlateEgg(input, unit, product);
			case PlateHeart.code:
				return new PlateHeart(input, unit, product);
			case PlateArrow.code:
				return new PlateArrow(input, unit, product);
			case PlatePolygon.code:
				return new PlatePolygon(input, unit, product);
			case PlateStar.code:
				return new PlateStar(input, unit, product);
			case PlateKite.code:
				return new PlateKite(input, unit, product);
			case PlateTrapezium.code:
				return new PlateTrapezium(input, unit, product);
			case PlateParallelogram.code:
				return new PlateParallelogram(input, unit, product);
			case PlateHexagon.code:
				return new PlateHexagon(input, unit, product);

			/** Shelf */
			case ShelfComplete.code:
				return new ShelfComplete(input, unit, product);
			case ShelfCustom.code:
				return new ShelfCustom(input, unit, product);

			/** Skirting */
			case SkirtingBlock.code:
				return new SkirtingBlock(input, unit, product);
			case SkirtingFrameComplete.code:
				return new SkirtingFrameComplete(input, unit, product);
			case SkirtingFrameCustom.code:
				return new SkirtingFrameCustom(input, unit, product);
			case SkirtingLyingComplete.code:
				return new SkirtingLyingComplete(input, unit, product);
			case SkirtingLyingCustom.code:
				return new SkirtingLyingCustom(input, unit, product);
			case SkirtingStandingComplete.code:
				return new SkirtingStandingComplete(input, unit, product);
			case SkirtingStandingCustom.code:
				return new SkirtingStandingCustom(input, unit, product);

			/** ThreeDimensional */
			case ThreedHollowshelf.code:
				return new ThreedHollowshelf(input, unit, product);
			case ThreedCubeClosed.code:
				return new ThreedCubeClosed(input, unit, product);
			case ThreedCubeOpen.code:
				return new ThreedCubeOpen(input, unit, product);
			case ThreedPillarClosed.code:
				return new ThreedPillarClosed(input, unit, product);
			case ThreedPillarOpen.code:
				return new ThreedPillarOpen(input, unit, product);

			/** Beam */
			case BeamComplete.code:
				return new BeamComplete(input, unit, product);
			case BeamShortened.code:
				return new BeamShortened(input, unit, product);

			default:
				throw new Error(`No shape model found for code: ${input.code}`);
		}
	},
};

export default ShapeFactory;
